import { useState, useId, useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Tooltip } from "@mui/material";
import i18n from "../../../../i18n";
import { baseTransKey } from "./verityAssessmentHelper";

import UndoIcon from "../../../../images/quiz-question/rich-text-menu/undo.svg?react";
import RedoIcon from "../../../../images/quiz-question/rich-text-menu/redo.svg?react";
import BoldIconRaw from "../../../../images/quiz-question/rich-text-menu/bold.svg?raw";
import ItalicIconRaw from "../../../../images/quiz-question/rich-text-menu/italic.svg?raw";
import UnderlineIconRaw from "../../../../images/quiz-question/rich-text-menu/underline.svg?raw";
import FontColorIconRaw from "../../../../images/quiz-question/rich-text-menu/font-color-icon.svg?raw";
import HighlightColorIconRaw from "../../../../images/quiz-question/rich-text-menu/highlight-color-icon.svg?raw";
import ClearFormattingIconRaw from "../../../../images/quiz-question/rich-text-menu/clear-formating.svg?raw";
import LaTeXIconRaw from "../../../../images/quiz-question/rich-text-menu/LaTeX_logo.svg?raw";

import styles from "./QuestionRichTextEditor.module.css";

// Quill's formula module requires katex on window
(window as Window & { katex?: typeof katex }).katex = katex;

// Register blank embed blot (runs once at module load)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EmbedBlot = Quill.import("blots/embed") as any;
class BlankBlot extends EmbedBlot {
	static blotName = "blank";
	static tagName = "span";
	static className = "question-blank";
	static create(value: number): HTMLElement {
		const node = super.create() as HTMLElement;
		node.setAttribute("data-id", String(value));
		node.textContent = `(${i18n.t(`${baseTransKey}.questions.editor.fitb.blankLabel`, { id: value })})`;
		return node;
	}
	static value(node: HTMLElement): number {
		return parseInt(node.getAttribute("data-id") ?? "0", 10);
	}
}

Quill.register(BlankBlot);

// Register custom icons in Quill's icon registry (runs once at module load)
const QuillIcons = Quill.import("ui/icons") as Record<string, string>;
QuillIcons["bold"] = BoldIconRaw;
QuillIcons["italic"] = ItalicIconRaw;
QuillIcons["underline"] = UnderlineIconRaw;
QuillIcons["color"] = FontColorIconRaw;
QuillIcons["background"] = HighlightColorIconRaw;
QuillIcons["clean"] = ClearFormattingIconRaw;
QuillIcons["formula"] = LaTeXIconRaw;

const QUILL_FORMATS = ["bold", "italic", "underline", "color", "background", "formula", "blank"];
const DEFAULT_MAX_LENGTH = 1000;

function ToolbarTooltip({
	id,
	title,
	activeId,
	setActiveId,
	children,
	isPickerTrigger = false,
	disabled = false,
}: {
	id: string;
	title: string;
	activeId: string | null;
	setActiveId: (id: string | null) => void;
	children: React.ReactNode;
	isPickerTrigger?: boolean;
	disabled?: boolean;
}) {
	return (
		<Tooltip title={title} open={activeId === id && !disabled} placement="bottom">
			<span
				style={{ display: "inline-block" }}
				onMouseEnter={(e) => {
					if (disabled) return;
					const pickerOpen = isPickerTrigger && e.currentTarget.querySelector(".ql-expanded") !== null;
					if (!pickerOpen) setActiveId(id);
				}}
				onMouseLeave={() => setActiveId(null)}
				onFocus={() => setActiveId(id)}
				onBlur={() => setActiveId(null)}
			>
				{children}
			</span>
		</Tooltip>
	);
}

export type QuestionRichTextEditorHandle = {
	insertBlank: (id: number) => void;
};

const QuestionRichTextEditor = forwardRef<
	QuestionRichTextEditorHandle,
	{
		value: string;
		onChange: (text: string) => void;
		placeholder?: string;
		maxLength?: number;
		toolbarEnd?: React.ReactNode;
	}
>(function QuestionRichTextEditor({ value, onChange, placeholder, maxLength = DEFAULT_MAX_LENGTH, toolbarEnd }, ref) {
	// Unique toolbar ID so multiple instances on the same page don't conflict
	const toolbarId = useId().replace(/:/g, "_");

	const quillRef = useRef<ReactQuill>(null);
	const [canUndo, setCanUndo] = useState(false);
	const [canRedo, setCanRedo] = useState(false);

	useImperativeHandle(
		ref,
		() => ({
			insertBlank(id: number) {
				const quill = quillRef.current?.getEditor();
				if (!quill) return;
				const range = quill.getSelection(true);
				const index = range ? range.index : quill.getLength() - 1;
				quill.insertText(index, "\u00a0", "user");
				quill.insertEmbed(index + 1, "blank", id, "user");
				// Use a non-breaking space so it isn't stripped by the HTML parser on re-parse
				quill.insertText(index + 2, "\u00a0", "user");
				quill.setSelection(index + 3, 0, "silent");
			},
		}),
		[],
	);

	useEffect(() => {
		const quill = quillRef.current?.getEditor();
		if (!quill) return;
		const updateHistory = () => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const { stack } = (quill as any).history as { stack: { undo: unknown[]; redo: unknown[] } };
			setCanUndo(stack.undo.length > 0);
			setCanRedo(stack.redo.length > 0);
		};
		quill.on("text-change", updateHistory);

		return () => {
			quill.off("text-change", updateHistory);
		};
	}, []);

	const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
	const ttUndo = useId();
	const ttRedo = useId();
	const ttBold = useId();
	const ttItalic = useId();
	const ttUnderline = useId();
	const ttColor = useId();
	const ttBackground = useId();
	const ttFormula = useId();
	const ttClean = useId();

	const quillModules = useMemo(
		() => ({
			toolbar: {
				container: `#${toolbarId}`,
				handlers: {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					undo(this: any) {
						this.quill.history.undo();
					},
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					redo(this: any) {
						this.quill.history.redo();
					},
				},
			},
			history: { delay: 500, maxStack: 100, userOnly: true },
			keyboard: {
				bindings: {
					// Return true so Quill does NOT call preventDefault(), letting the
					// browser handle Tab as normal focus navigation instead of inserting a tab.
					tab: { key: 9, handler: () => true },
				},
			},
		}),
		[toolbarId],
	);

	const getPlainTextFromHtml = (html: string) => {
		const doc = new DOMParser().parseFromString(html, "text/html");
		return doc.body.textContent ?? "";
	};
	const charCount = getPlainTextFromHtml(value).replace(/\n/g, "").length;

	return (
		<div className={styles.container}>
			<div id={toolbarId} className={styles.toolbar}>
				<span className="ql-formats">
					<ToolbarTooltip
						id={ttUndo}
						title={i18n.t(`${baseTransKey}.questions.editor.toolbar.undo`)}
						activeId={activeTooltip}
						setActiveId={setActiveTooltip}
						disabled={!canUndo}
					>
						<button className="ql-undo" disabled={!canUndo}>
							<UndoIcon />
						</button>
					</ToolbarTooltip>
					<ToolbarTooltip
						id={ttRedo}
						title={i18n.t(`${baseTransKey}.questions.editor.toolbar.redo`)}
						activeId={activeTooltip}
						setActiveId={setActiveTooltip}
						disabled={!canRedo}
					>
						<button className="ql-redo" disabled={!canRedo}>
							<RedoIcon />
						</button>
					</ToolbarTooltip>
					<ToolbarTooltip
						id={ttBold}
						title={i18n.t(`${baseTransKey}.questions.editor.toolbar.bold`)}
						activeId={activeTooltip}
						setActiveId={setActiveTooltip}
					>
						<button className="ql-bold" />
					</ToolbarTooltip>
					<ToolbarTooltip
						id={ttItalic}
						title={i18n.t(`${baseTransKey}.questions.editor.toolbar.italic`)}
						activeId={activeTooltip}
						setActiveId={setActiveTooltip}
					>
						<button className="ql-italic" />
					</ToolbarTooltip>
					<ToolbarTooltip
						id={ttUnderline}
						title={i18n.t(`${baseTransKey}.questions.editor.toolbar.underline`)}
						activeId={activeTooltip}
						setActiveId={setActiveTooltip}
					>
						<button className="ql-underline" />
					</ToolbarTooltip>
					<ToolbarTooltip
						id={ttColor}
						title={i18n.t(`${baseTransKey}.questions.editor.toolbar.textColor`)}
						activeId={activeTooltip}
						setActiveId={setActiveTooltip}
						isPickerTrigger
					>
						<select className="ql-color" />
					</ToolbarTooltip>
					<ToolbarTooltip
						id={ttBackground}
						title={i18n.t(`${baseTransKey}.questions.editor.toolbar.highlightColor`)}
						activeId={activeTooltip}
						setActiveId={setActiveTooltip}
						isPickerTrigger
					>
						<select className="ql-background" />
					</ToolbarTooltip>
					<ToolbarTooltip
						id={ttFormula}
						title={i18n.t(`${baseTransKey}.questions.editor.toolbar.insertFormula`)}
						activeId={activeTooltip}
						setActiveId={setActiveTooltip}
					>
						<button className="ql-formula" />
					</ToolbarTooltip>
					<ToolbarTooltip
						id={ttClean}
						title={i18n.t(`${baseTransKey}.questions.editor.toolbar.clearFormatting`)}
						activeId={activeTooltip}
						setActiveId={setActiveTooltip}
					>
						<button className="ql-clean" />
					</ToolbarTooltip>
					<div style={{ flex: 1 }} />
					{toolbarEnd}
				</span>
			</div>
			<ReactQuill
				ref={quillRef}
				theme="snow"
				value={value}
				onChange={(val, _delta, source) => {
					if (source === "user") onChange(val);
				}}
				placeholder={placeholder}
				modules={quillModules}
				formats={QUILL_FORMATS}
				className={styles.editor}
			/>
			<div className={styles.charCount}>
				{charCount}/{maxLength}
			</div>
		</div>
	);
});

export default QuestionRichTextEditor;
